// Hook for signing workflow with polling
// This encapsulates all the logic for email signing, status polling, and downloads

import { useState, useEffect, useRef, useCallback } from "react";
import type {
  HashResult,
  SigningState,
  SigningStatus,
  ProofType,
} from "../types";
import {
  signHash,
  getSigningStatus,
  checkEmailStatus,
  embedMetadata,
  downloadBlob,
} from "../api/client";

export interface UseSigningFlowReturn {
  // State
  email: string;
  setEmail: (email: string) => void;
  proofType: ProofType;
  setProofType: (type: ProofType) => void;
  registerOnChain: boolean;
  setRegisterOnChain: (value: boolean) => void;
  signingState: SigningState;
  signingStatus: SigningStatus | null;
  signingError: string | null;
  sendEmailNotification: boolean;
  setSendEmailNotification: (value: boolean) => void;
  emailNotificationsAvailable: boolean;
  downloadingEmail: boolean;
  downloadingProof: boolean;

  // Actions
  handleSign: () => Promise<void>;
  handleDownloadWithEmail: () => Promise<void>;
  handleDownloadWithProof: () => Promise<void>;
  reset: () => void;

  // Helpers
  isValidEmail: (email: string) => boolean;
}

export function useSigningFlow(
  image: File | null,
  hashes: HashResult | null
): UseSigningFlowReturn {
  // Email and proof type state
  const [email, setEmail] = useState("");
  const [proofType, setProofType] = useState<ProofType>(null);
  const [registerOnChain, setRegisterOnChain] = useState(false);

  // Signing state
  const [signingState, setSigningState] = useState<SigningState>("idle");
  const [signingError, setSigningError] = useState<string | null>(null);
  const [signingRequestId, setSigningRequestId] = useState<string | null>(null);
  const [signingStatus, setSigningStatus] = useState<SigningStatus | null>(
    null
  );

  // Email notification state
  const [sendEmailNotification, setSendEmailNotification] = useState(false);
  const [emailNotificationsAvailable, setEmailNotificationsAvailable] =
    useState(false);

  // Download state
  const [downloadingEmail, setDownloadingEmail] = useState(false);
  const [downloadingProof, setDownloadingProof] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Polling ref
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  // Check if email notifications are available
  useEffect(() => {
    const check = async () => {
      const result = await checkEmailStatus();
      setEmailNotificationsAvailable(result.configured);
    };
    check();
  }, []);

  // Poll for signing status
  const pollStatus = useCallback(async (requestId: string) => {
    return await getSigningStatus(requestId);
  }, []);

  useEffect(() => {
    if (
      !signingRequestId ||
      signingState === "idle" ||
      signingState === "error"
    ) {
      return;
    }

    const doPoll = async () => {
      const status = await pollStatus(signingRequestId);
      if (status) {
        setSigningStatus(status);

        // Stop polling based on selected proof type
        let shouldStop = false;
        if (proofType === "direct") {
          shouldStop = !!status.raw_email;
        } else if (proofType === "zk") {
          if (registerOnChain) {
            shouldStop = !!status.proof && !!status.tx_hash;
          } else {
            shouldStop = !!status.proof;
          }
        }

        if (shouldStop) {
          setSigningState("success");
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        } else if (status.error) {
          setSigningState("error");
          setSigningError(status.error);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        } else {
          // Mark as success when the required field is available
          if (proofType === "direct" && status.raw_email) {
            setSigningState("success");
          } else if (proofType === "zk" && status.proof) {
            if (registerOnChain) {
              if (status.tx_hash) {
                setSigningState("success");
              }
            } else {
              setSigningState("success");
            }
          }
        }
      }
    };

    doPoll(); // Poll immediately
    pollingIntervalRef.current = setInterval(doPoll, 3000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [signingRequestId, signingState, pollStatus, proofType, registerOnChain]);

  const reset = useCallback(() => {
    setEmail("");
    setSigningState("idle");
    setSigningError(null);
    setSigningRequestId(null);
    setSigningStatus(null);
    setProofType(null);
    setRegisterOnChain(false);
    setSendEmailNotification(false);
    setDownloadError(null);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const handleSign = useCallback(async () => {
    if (!hashes || !email || !proofType) return;

    setSigningState("signing");
    setSigningError(null);
    setSigningRequestId(null);
    setSigningStatus(null);

    try {
      const result = await signHash({
        email,
        hash: hashes.dinohash512.hex,
        hash96: hashes.dinohash96.hex,
        enableZk: proofType === "zk",
        registerOnChain: proofType === "zk" ? registerOnChain : false,
        sendEmailNotification,
        image: sendEmailNotification ? image || undefined : undefined,
      });

      if (result.id) {
        setSigningRequestId(result.id);
        setSigningState("pending");
      } else {
        throw new Error("No request ID received from signing service");
      }
    } catch (err) {
      setSigningState("error");
      setSigningError(err instanceof Error ? err.message : "An error occurred");
    }
  }, [
    hashes,
    email,
    proofType,
    registerOnChain,
    sendEmailNotification,
    image,
  ]);

  const handleDownloadWithEmail = useCallback(async () => {
    if (!image || !hashes || !signingStatus?.raw_email) return;

    setDownloadingEmail(true);
    setDownloadError(null);

    try {
      const { blob, filename } = await embedMetadata({
        image,
        hash96: hashes.dinohash96.hex,
        hash512: hashes.dinohash512.hex,
        rawEmail: signingStatus.raw_email,
      });
      downloadBlob(blob, filename);
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : "Failed to download"
      );
    } finally {
      setDownloadingEmail(false);
    }
  }, [image, hashes, signingStatus]);

  const handleDownloadWithProof = useCallback(async () => {
    if (!image || !hashes || !signingStatus?.proof) return;

    setDownloadingProof(true);
    setDownloadError(null);

    try {
      const { blob, filename } = await embedMetadata({
        image,
        hash96: hashes.dinohash96.hex,
        hash512: hashes.dinohash512.hex,
        proof: signingStatus.proof,
      });
      downloadBlob(blob, filename);
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : "Failed to download"
      );
    } finally {
      setDownloadingProof(false);
    }
  }, [image, hashes, signingStatus]);

  const isValidEmail = useCallback((email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, []);

  return {
    email,
    setEmail,
    proofType,
    setProofType,
    registerOnChain,
    setRegisterOnChain,
    signingState,
    signingStatus,
    signingError,
    sendEmailNotification,
    setSendEmailNotification,
    emailNotificationsAvailable,
    downloadingEmail,
    downloadingProof,
    handleSign,
    handleDownloadWithEmail,
    handleDownloadWithProof,
    reset,
    isValidEmail,
  };
}
